import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminEvent } from '@/app/admin/AdminEventContext';
import { adminApi } from '@/features/admin/api/adminApi';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { ApiError } from '@/shared/api/httpClient';
import type { ExhibitorDto } from '@/shared/types/domain';

export function ExhibitorsListPage() {
  const { eventId, isLoading: isEventLoading } = useAdminEvent();
  const navigate = useNavigate();
  const [exhibitors, setExhibitors] = useState<ExhibitorDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  function refetch() {
    if (!eventId) return;
    setIsLoading(true);
    adminApi.exhibitors.listByEvent(eventId).then(setExhibitors).finally(() => setIsLoading(false));
  }

  useEffect(refetch, [eventId]);

  if (isEventLoading) return <Spinner label="Loading" />;
  if (!eventId) return <EmptyState icon="◧" title="No events yet" description="Create an event first." />;

  return (
    <div>
      <div className="admin-page__header">
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          {exhibitors.length} exhibitor{exhibitors.length === 1 ? '' : 's'}
        </p>
        <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? 'Cancel' : '+ New exhibitor'}</Button>
      </div>

      {showCreate && (
        <CreateExhibitorForm
          eventId={eventId}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {isLoading ? (
        <Spinner label="Loading exhibitors" />
      ) : exhibitors.length === 0 ? (
        <EmptyState icon="▦" title="No exhibitors yet" description="Add the first exhibitor for this event above." />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Status</th>
              <th>Staff</th>
              <th>Scans</th>
            </tr>
          </thead>
          <tbody>
            {exhibitors.map((ex) => (
              <tr key={ex.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/exhibitors/${ex.id}`)}>
                <td style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  {ex.brandColorHex && (
                    <span
                      aria-hidden="true"
                      style={{ width: 10, height: 10, borderRadius: '50%', background: ex.brandColorHex, display: 'inline-block' }}
                    />
                  )}
                  {ex.companyName}
                </td>
                <td>{ex.isActive ? 'Active' : 'Inactive'}</td>
                <td>{ex.staffCount}</td>
                <td>{ex.scanCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreateExhibitorForm({ eventId, onCreated }: { eventId: string; onCreated: () => void }) {
  const [companyName, setCompanyName] = useState('');
  const [brandColorHex, setBrandColorHex] = useState('#17D7C4');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.exhibitors.create({ eventId, companyName, brandColorHex });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not create exhibitor.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'flex-end',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-5)',
        flexWrap: 'wrap'
      }}
    >
      {error && <div className="error-banner" style={{ flexBasis: '100%' }}>{error}</div>}
      <div className="field" style={{ flex: 1, minWidth: 200 }}>
        <label className="field__label">Company name</label>
        <input className="field__input" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Brand color</label>
        <input
          type="color"
          className="field__input"
          style={{ width: 64, padding: 4 }}
          value={brandColorHex}
          onChange={(e) => setBrandColorHex(e.target.value)}
        />
      </div>
      <Button type="submit" isLoading={isSaving}>
        Create
      </Button>
    </form>
  );
}